import type { DocumentPartyRef } from '@klear/schema/document-processing'
import { isFilingDocument } from '@klear/schema/document-review-step'
import type { DocumentReviewDetailOutput } from '@/app/document-review/use-document-review'

function count(amount: number, singular: string, plural: string): string {
  return `${amount} ${amount === 1 ? singular : plural}`
}

function partyToCreate(party: DocumentPartyRef): string | null {
  return party.kind === 'create' ? party.party.name : null
}

function partyPhrases(detail: DocumentReviewDetailOutput): string[] {
  const phrases: string[] = []
  if (detail.order !== null && detail.order.kind === 'create') {
    phrases.push('Creates the order')
    const customer = partyToCreate(detail.order.customer)
    if (customer !== null) phrases.push(`Creates the customer ${customer}`)
  }
  return phrases
}

export function commitPhrases(detail: DocumentReviewDetailOutput): string[] {
  const creates = detail.proposals.filter((one) => one.record?.kind === 'create').length
  const joins = detail.proposals.filter(
    (one) => one.record?.kind === 'attach' && one.record.fileDocument !== false
  ).length
  const updates = detail.proposals.filter(
    (one) => one.record?.kind === 'attach' && one.record.fileDocument === false
  ).length
  const onPayables = detail.proposals.filter((one) => one.record?.kind === 'attach_payment').length
  const questions = detail.proposals.filter((one) => one.record === null && one.kind !== 'trader_document').length
  const filesAsUpload = detail.proposals.some((one) => one.kind === 'trader_document')

  const phrases: string[] = []
  if (creates > 0) phrases.push(`Creates ${count(creates, 'record', 'records')}`)
  if (joins > 0) phrases.push(`Joins ${count(joins, 'existing record', 'existing records')}`)
  if (updates > 0) phrases.push(`Updates ${count(updates, 'existing record', 'existing records')}`)
  if (onPayables > 0) phrases.push(`Files on ${count(onPayables, 'existing payable', 'existing payables')}`)

  phrases.push(...partyPhrases(detail))

  const recording = detail.proposals.filter((one) => one.record !== null)
  const filing = recording.filter((one) => isFilingDocument(one.record)).length
  if (filesAsUpload) phrases.push('Files the document as an upload')
  else if (filing === recording.length && recording.length > 0) phrases.push('Attaches the document to each')
  else if (filing > 0) phrases.push(`Attaches the document to ${count(filing, 'record', 'records')}`)
  if (questions > 0) phrases.push(`Leaves ${count(questions, 'question', 'questions')} unanswered`)

  return phrases
}

export type CommitEntry = { action: string; what: string; amount: string | null }
